import { useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Button,
  ListItem,
  Stack,
  Typography,
  TextField,
  Box,
} from "@mui/material";
import { SxProps, useTheme } from "@mui/material/styles";
import { useState } from "react";

export type DropdownOptions = {
  label: string;
  value: string;
};

type Props = {
  items: DropdownOptions[];
  loading?: boolean;
  onSelectItem: Function;
  selected: string | undefined;
  label?: string;
  error?: boolean;
  helperText?: string | boolean;
  isEdited?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  width?: string;
  sx?: SxProps;
  maxWidthOption?: string;
  minWidthOption?: string;
  defaultValue?: DropdownOptions | null;
  fullWidth?: boolean;
};

function AutoCompleteSelect(props: Props) {
  const {
    items,
    onSelectItem,
    selected,
    label,
    loading,
    error,
    helperText,
    isEdited,
    placeholder,
    disabled,
    required,
    width,
    maxWidthOption,
    minWidthOption,
    defaultValue,
    fullWidth = false,
    ...restProps
  } = props;
  const [inputValue, setInputValue] = useState("");
  const theme = useTheme();

  useEffect(() => {
    if (defaultValue) {
      setInputValue(defaultValue.label);
    } else {
      setInputValue("");
    }
  }, [defaultValue]);

  useEffect(() => {
    if (selected !== undefined) {
      const selectedItem = items.find((item) => item.value.toString() === selected);
      if (selectedItem) {
        setInputValue(selectedItem.label);
      }
    }
  }, [selected, items]);

  return (
    <Autocomplete
      disablePortal
      options={items}
      disabled={disabled}
      loading={loading}
      defaultValue={defaultValue}
      getOptionLabel={(option) => option.label || ""}
      onChange={(event, value) => {
        onSelectItem(value?.value || null);
      }}
      value={items.find((item) => item.value.toString() === selected) || null}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      fullWidth={fullWidth}
      ListboxProps={{
        style: { maxHeight: 400, overflowY: "auto" },
      }}
      size="small"
      sx={{ m: 1, minWidth: width ?? "120px", maxWidth: width }}
      {...restProps}
      renderInput={(params) => (
        <Box className="input-wrapper">
          <TextField
            {...params}
            error={error}
            className={isEdited ? "not-edited" : ""}
            placeholder={placeholder || "Search..."}
            label={label}
            required={required}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <Typography style={{ fontSize: 12 }} color={theme.palette.error.main}>
            {helperText}
          </Typography>
          {isEdited && (
            <Button
              className="close_btn"
              onClick={() => {
                onSelectItem("");
                setInputValue("");
              }}
            >
              <CloseIcon />
            </Button>
          )}
        </Box>
      )}
      renderOption={(props, option) => {
        return (
          <ListItem
            {...props}
            key={option.value}
            style={{
              minWidth: minWidthOption ?? "200px",
              maxWidth: maxWidthOption,
            }}
          >
            <Stack>
              <Typography>
                {option?.label?.length > 40
                  ? `${option?.label?.substring(0, 40)}...`
                  : option?.label}
              </Typography>
            </Stack>
          </ListItem>
        );
      }}
    />
  );
}

export default AutoCompleteSelect;